import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  forkJoin,
  EMPTY,
  Observable,
  of,
  catchError,
  filter,
  first,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { ToastsContainerComponent } from 'src/app/core/components/toast/toasts-container.component';
import { Constants } from 'src/app/core/constants';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { InventoryItemFileView } from 'src/app/core/models/inventory/inventory-item-file-view.interface';
import { Attachment, AttachmentWithDetails } from 'src/app/core/models/project/attachment/attachment.interface';
import { StorageUsage } from 'src/app/core/models/storage-usage.interface';
import { FileService } from 'src/app/core/services/file.service';
import { InventoryItemAttachmentService } from 'src/app/core/services/inventory-item-attachment.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { TariffService } from 'src/app/core/services/tariff.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Convertor } from 'src/app/core/utils/convertor';
import { Util } from 'src/app/core/utils/util';

@Component({
  selector: 'app-inventory-item-files-tab',
  templateUrl: './inventory-item-files-tab.component.html',
  styleUrl: './inventory-item-files-tab.component.scss',
  standalone: true,
  imports: [CommonModule, CoreModule, ToastsContainerComponent],
})
export class InventoryItemFilesTabComponent implements OnChanges {
  private readonly fileService = inject(FileService);
  private readonly inventoryItemAttachmentService = inject(InventoryItemAttachmentService);
  private readonly permissionService = inject(PermissionService);
  private readonly tariffService = inject(TariffService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(NgbModal);

  private readonly imageSlotIds = ['title', 'image-2', 'image-3', 'image-4'] as const;

  @Input({ required: true }) companyId = '';
  @Input({ required: true }) itemId = '';
  @Input() galleryRefreshToken = 0;
  @Input() isReadonly = false;
  @Output() imageFilesChanged = new EventEmitter<void>();

  @ViewChild('dangerTpl') dangerTpl!: TemplateRef<unknown>;

  public readonly maxFileSize = Constants.RequestBodySize;
  public readonly currentLang: string;
  public readonly emptyFileListParam: Record<string, string> = {
    uk: 'файлах',
    en: 'File list',
  };
  public readonly storageLimitParam: Record<string, string> = {
    uk: 'сховища',
    en: 'storage',
  };

  public callbackError?: string;
  public attachmentsPath = '';
  public uploadPath = '';
  public galleryImagesPath = '';

  private readonly filesSubject$ = new BehaviorSubject<InventoryItemFileView[]>([]);

  public readonly canEditFiles$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Company,
        UserCompanyPermissions.Edit,
      ),
    ),
    shareReplay(1),
  );

  public readonly canUploadFiles$ = this.canEditFiles$.pipe(
    switchMap((canEditFiles) =>
      this.tariffService.companyQuotas$.pipe(
        map((companyQuotas) => {
          const quotaKey = Convertor.pascalToScreamingSnakeCase(Constants.QuotaKeys.s3Size);
          const maxStorageSizeQuota = companyQuotas.find((quota) => quota.key === quotaKey);

          if (!maxStorageSizeQuota) {
            return false;
          }

          return canEditFiles && maxStorageSizeQuota.limit > maxStorageSizeQuota.used;
        }),
      ),
    ),
    shareReplay(1),
  );

  public readonly storageUsage$ = this.tariffService.companyQuotas$.pipe(
    map((companyQuotas): StorageUsage => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(Constants.QuotaKeys.s3Size);
      const storageQuota = companyQuotas.find((quota) => quota.key === quotaKey);
      const limit = storageQuota?.limit ?? 0;
      const used = storageQuota?.used ?? 0;
      const available = Math.max(limit - used, 0);
      const usedPercent = limit > 0 ? Math.round((used / limit) * 1000) / 10 : 0;

      return {
        used,
        limit,
        available,
        usedPercent,
      };
    }),
    shareReplay(1),
  );

  public readonly files$ = this.filesSubject$.pipe(
    map((files): AttachmentWithDetails[] =>
      files.map((file): AttachmentWithDetails => ({
        ...file,
        imageSrc: Util.getAttachmentImage(file.title),
        extensionName: Util.getExtensionFromFileTitle(file.title),
        fileActionName: Util.canBeOpenedFile(file) ? 'open' : 'download',
        uploadedByName: file.createdBy ?? null,
        uploadedAtLabel: this.formatAttachmentUploadedAt(file.createdAt),
        canRename: file.source === 'attachment',
        canRemove: true,
      })),
    ),
    shareReplay(1),
  );

  constructor() {
    this.currentLang = this.translateService.currentLang ?? Constants.DefaultLanguage;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId'] && !changes['itemId'] && !changes['galleryRefreshToken']) {
      return;
    }

    if (!this.companyId || !this.itemId) {
      return;
    }

    this.attachmentsPath = `companies/${this.companyId}/inventory-items/${this.itemId}`;
    this.uploadPath = this.attachmentsPath;
    this.galleryImagesPath = `${this.attachmentsPath}/gallery`;
    this.inventoryItemAttachmentService.setAttachmentsUrl(this.companyId, this.itemId);
    this.reloadAttachments();
  }

  public reloadAttachments(): void {
    forkJoin({
      attachments: this.inventoryItemAttachmentService.getInventoryItemAttachments(),
      imageFiles: this.loadImageFiles(),
    })
      .pipe(
        first(),
        tap(({ attachments, imageFiles }) => {
          this.filesSubject$.next([
            ...attachments.map((attachment) => this.mapAttachmentToFileView(attachment)),
            ...imageFiles,
          ]);
        }),
      )
      .subscribe();
  }

  public onUploadFile(event: CustomFile): void {
    if (!event.upload) {
      return;
    }

    const attachmentId = Util.getGuidFromTitle(event.title)
      ?? globalThis.crypto?.randomUUID?.()
      ?? Util.generateGUID();

    const newFile: Attachment = {
      id: attachmentId,
      title: event.file.name,
      fileSize: event.file.size,
      companyId: this.companyId,
      isFolder: false,
      uploadId: event.upload.uploadId,
      s3Bucket: event.upload.bucket,
      s3Key: event.upload.key,
      eTag: event.upload.eTag,
      versionId: event.upload.versionId,
      contentType: event.upload.contentType,
      createdAtUtc: event.upload.createdAtUtc,
    };

    const updatedFiles = [...this.filesSubject$.getValue(), this.mapAttachmentToFileView(newFile)];

    this.inventoryItemAttachmentService
      .createAttachment(newFile, event.upload.correlationId)
      .pipe(
        first(),
        tap((createdAttachment) => {
          const createdFile = updatedFiles.find((file) => file.id === createdAttachment.id);

          if (createdFile) {
            createdFile.createdAt = createdAttachment.createdAt ?? null;
            createdFile.createdBy = createdAttachment.createdBy ?? null;
          }

          this.filesSubject$.next(updatedFiles);
        }),
        catchError(() => this.rollbackUploadedAttachment(event.upload!.key)),
      )
      .subscribe();
  }

  public onDeleteFile(event: CustomFile): void {
    const fileToDelete = this.findUploadedFile(event);
    if (!fileToDelete) {
      return;
    }

    this.filesSubject$.next(this.filesSubject$.getValue().filter((file) => file.id !== fileToDelete.id));

    if (fileToDelete.source !== 'attachment') {
      return;
    }

    this.inventoryItemAttachmentService.deleteAttachment(fileToDelete.id).pipe(
      first(),
      catchError(() => {
        this.reloadAttachments();
        return EMPTY;
      }),
    ).subscribe();
  }

  public deleteAttachment(id: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      'WARNINGS.DELETE',
      {
        uk: 'файл',
        en: 'file',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((result) => !!result),
        switchMap(() => {
          const files = this.filesSubject$.getValue();
          const fileToDelete = files.find((file) => file.id === id);

          if (!fileToDelete) {
            return EMPTY;
          }

          const path = fileToDelete.s3Key ?? `${this.attachmentsPath}/${Util.getFileFullName(fileToDelete)}`;
          const updatedFiles = files.filter((file) => file.id !== id);

          return this.fileService.delete(path).pipe(
            first(),
            tap(() => {
              this.filesSubject$.next(updatedFiles);

              if (fileToDelete.source === 'inventoryImage') {
                this.imageFilesChanged.emit();
              }
            }),
            switchMap(() =>
              fileToDelete.source === 'attachment'
                ? this.inventoryItemAttachmentService.deleteAttachment(id)
                : of(null),
            ),
            catchError(() => {
              this.reloadAttachments();
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe();
  }

  public handleFileAction(file: Attachment, action: 'open' | 'download'): void {
    const path = file.s3Key ?? `${this.attachmentsPath}/${Util.getFileFullName(file)}`;
    const traceOptions = {
      uploadId: file.uploadId,
      fileId: file.id,
    };

    const fileAction$: Observable<string | Blob> =
      action === 'open'
        ? this.fileService.getFileUrl(path, false, traceOptions).pipe(
            tap((url) => window.open(url, '_blank')),
          )
        : this.fileService.downloadFile(path, file.title, traceOptions).pipe(
            tap((blob) => Util.saveAs(blob, file.title)),
          );

    fileAction$
      .pipe(
        first(),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public renameFile(id: string, newFileName: string): void {
    this.inventoryItemAttachmentService
      .renameAttachment(id, newFileName)
      .pipe(
        first(),
        tap(() => {
          const files = this.filesSubject$.getValue();
          const updatedAttachment = files.find((attachment) => attachment.id === id);

          if (!updatedAttachment) {
            return;
          }

          updatedAttachment.mappedTitle = newFileName;
          this.filesSubject$.next([...files]);
        }),
      )
      .subscribe();
  }

  public onReachedStorageLimit(): void {
    this.callbackError = this.translateService.instant('TOOLTIPS.LIMIT_REACHED', {
      entity: this.storageLimitParam[this.currentLang],
    });

    this.showDangerToast();
  }

  private rollbackUploadedAttachment(s3Key: string): Observable<never> {
    return this.fileService.delete(s3Key).pipe(
      first(),
      catchError(() => of(null)),
      tap(() => {
        this.callbackError = this.translateService.instant('ERRORS.FILE_UPLOAD_METADATA_SAVE_FAILED');
        this.showDangerToast();
      }),
      switchMap(() => EMPTY),
    );
  }

  private showDangerToast(): void {
    this.toastService.show({
      template: this.dangerTpl,
      className: 'bg-danger text-light',
    });
  }

  private formatAttachmentUploadedAt(createdAt?: string | null): string | null {
    if (!createdAt) {
      return null;
    }

    return new Intl.DateTimeFormat(this.translateService.currentLang || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(createdAt));
  }

  private loadImageFiles(): Observable<InventoryItemFileView[]> {
    return forkJoin({
      rootKeys: this.fileService.listFiles(this.attachmentsPath).pipe(catchError(() => of([]))),
      galleryKeys: this.fileService.listFiles(this.galleryImagesPath).pipe(catchError(() => of([]))),
    }).pipe(
      map(({ rootKeys, galleryKeys }) =>
        [...new Set([...rootKeys, ...galleryKeys])]
          .map((key) => this.mapImageKeyToFileView(key))
          .filter((file): file is InventoryItemFileView => file !== null),
      ),
    );
  }

  private mapAttachmentToFileView(attachment: Attachment): InventoryItemFileView {
    return {
      ...attachment,
      source: 'attachment',
    };
  }

  private mapImageKeyToFileView(key: string): InventoryItemFileView | null {
    const rootPathPrefix = `${this.attachmentsPath}/`;
    const galleryPathPrefix = `${this.galleryImagesPath}/`;
    const isRootImage = key.startsWith(rootPathPrefix) && !key.startsWith(galleryPathPrefix);
    const isGalleryImage = key.startsWith(galleryPathPrefix);

    if (!isRootImage && !isGalleryImage) {
      return null;
    }

    const fileName = isGalleryImage ? key.slice(galleryPathPrefix.length) : key.slice(rootPathPrefix.length);
    const extension = Util.getExtensionFromFileTitle(fileName);
    if (!extension) {
      return null;
    }

    const slotId = fileName.slice(0, -(extension.length + 1));
    if (!this.imageSlotIds.includes(slotId as (typeof this.imageSlotIds)[number])) {
      return null;
    }

    if (isRootImage && slotId !== 'title') {
      return null;
    }

    if (isGalleryImage && slotId === 'title') {
      return null;
    }

    return {
      id: `image:${slotId}`,
      title: fileName,
      fileSize: 0,
      isFolder: false,
      s3Key: key,
      source: 'inventoryImage',
    };
  }

  private findUploadedFile(event: CustomFile): InventoryItemFileView | undefined {
    const parsedId = Util.getGuidFromTitle(event.title);
    if (parsedId) {
      return this.filesSubject$.getValue().find((file) => file.id === parsedId);
    }

    const normalizedKey = `${this.attachmentsPath}/${event.title}.${Util.getExtensionFromFileTitle(event.file.name)}`;
    return this.filesSubject$.getValue().find((file) => file.s3Key === normalizedKey);
  }
}
